import React from 'react';
import { Box, Chip, Link, Typography } from '@mui/material';
import moment from 'moment';
import { renderStatus } from '../../../components/v2/cell-renderers/status';
import { formatKg, formatKgMetric } from './constants';

export const cellContentSx = {
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  height: '100%',
  minWidth: 0,
  width: '100%',
  py: 0.5,
};

const nsirLinkSx = {
  color: 'info.main',
  fontWeight: 600,
  textDecoration: 'underline',
  '&:hover': { color: 'info.dark' },
};

const resolveMateriaisList = (row) => {
  if (Array.isArray(row.materiais) && row.materiais.length > 0) {
    return row.materiais
      .map((item) => ({
        key: item.id || item.tipo || item.label || item.tipoNome,
        label: item.label || item.tipoNome || item.tipo || '—',
        pesoKg: Number(item.pesoKg) || 0,
      }))
      .filter((item) => item.pesoKg > 0)
      .sort((a, b) => b.pesoKg - a.pesoKg);
  }

  const byMaterial = row.byMaterial;
  if (byMaterial && typeof byMaterial === 'object') {
    return Object.entries(byMaterial)
      .map(([tipo, pesoKg]) => ({
        key: tipo,
        label: row.materialLabels?.[tipo] || tipo,
        pesoKg: Number(pesoKg) || 0,
      }))
      .filter((item) => item.pesoKg > 0)
      .sort((a, b) => b.pesoKg - a.pesoKg);
  }

  return [];
};

const isDeclaracaoSemSucata = (row) => {
  if (row.semSucata === true || row.semSucata === 'true') return true;
  if (row.totalKg == null || row.totalKg === '') return false;
  if (Number(row.totalKg) !== 0) return false;
  return resolveMateriaisList(row).length === 0;
};

export const renderNsirCodigo = (row) => {
  const { id, codigo } = row;
  if (!codigo) return '—';
  if (!id) return codigo;
  const href = `${window.location.origin}/v2/nsir/${id}`;
  return (
    <Link
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      onClick={(e) => e.stopPropagation()}
      sx={nsirLinkSx}
    >
      {codigo}
    </Link>
  );
};

export const renderNsirCol = (row) => (
  <Box sx={cellContentSx}>
    <Typography variant="body2" noWrap>
      <Box component="span" sx={{ fontWeight: 700 }}>Lote: </Box>
      {renderNsirCodigo(row)}
    </Typography>
    {row.malhaCodigo && (
      <Typography variant="body2" color="text.secondary" noWrap>
        Região: {row.malhaCodigo}
      </Typography>
    )}
  </Box>
);

export const renderStatusCol = (row) => (
  <Box sx={{ ...cellContentSx, alignItems: 'flex-start' }}>
    {row.status
      ? renderStatus({
          value: row.status,
          label: row.statusName || row.status,
        })
      : '—'}
  </Box>
);

export const renderPeriodoCol = (row) => {
  const label =
    row.periodoLabel
    || (row.periodoInicio && row.periodoFim
      ? `${moment(row.periodoInicio).format('DD/MM/YYYY')} – ${moment(row.periodoFim).format('DD/MM/YYYY')}`
      : '—');
  return (
    <Box sx={cellContentSx}>
      <Typography variant="body2">{label}</Typography>
    </Box>
  );
};

export const renderPesoCol = (row) => {
  const materiais = resolveMateriaisList(row);
  const totalLabel = formatKg(row.totalKg);
  const semSucata = isDeclaracaoSemSucata(row);

  return (
    <Box sx={cellContentSx}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        Total: {totalLabel === '—' ? '0,00' : totalLabel} kg
      </Typography>
      {semSucata ? (
        <Chip
          label="Sem sucata"
          size="small"
          variant="outlined"
          sx={{ mt: 0.5, alignSelf: 'flex-start', height: 22 }}
        />
      ) : materiais.length > 0 ? (
        materiais.map((item, index) => (
          <Typography
            key={item.key != null ? `${item.key}-${item.pesoKg}` : `${item.label}-${item.pesoKg}-${index}`}
            variant="caption"
            color="text.secondary"
            component="div"
            sx={{ lineHeight: 1.35 }}
          >
            · {item.label}: {formatKgMetric(item.pesoKg)} kg
          </Typography>
        ))
      ) : (
        row.quantidadeMateriais != null && row.quantidadeMateriais > 0 && (
          <Typography variant="caption" color="text.secondary">
            {row.quantidadeMateriais} material(is)
          </Typography>
        )
      )}
    </Box>
  );
};
